import {
  Component,
  inject,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { InitialsPipe } from '@app/shared/pipes/initials.pipe';
import { Pagination } from '@app/shared/components/api/models/pagination.model';
import { DataVoidComponent } from '../data-void/data-void.component';
import { Textarea } from 'primeng/textarea';
import { UserService } from '@app/auth/services/user.service';
import { ComentarioService } from '../api/services/comentario.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-lista-comentarios',
  imports: [
    CommonModule,
    Menu,
    ButtonModule,
    FormsModule,
    ReactiveFormsModule,
    DataVoidComponent,
    InitialsPipe,
    Textarea,
    ConfirmDialogModule,
  ],
  providers: [ComentarioService, ConfirmationService],
  standalone: true,
  templateUrl: './lista-comentarios.component.html',
  styleUrl: './lista-comentarios.component.scss',
})
export class ListaComentariosComponent implements OnInit, OnChanges {
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);
  fb = inject(FormBuilder);
  userService = inject(UserService);
  comentarioService = inject(ComentarioService);
  pagination: Pagination<any> = new Pagination<any>();
  @Input() comments: any[] = [];
  @Input() campanhaID!: string;
  @Output() commentChanged = new EventEmitter<void>();
  UserID: any;
  showComment: number | undefined = 0;
  formEditComment!: FormGroup;
  novoConteudoComentario: string = '';
  isSubmittingNovo: boolean = false;
  userInitials: string = '';

  items: MenuItem[] = [
    { label: 'Editar', icon: 'pi pi-pencil' },
    { label: 'Eliminar', icon: 'pi pi-trash' },
  ];

  ngOnInit(): void {
    this.pagination = new Pagination<any>().deserialize(this.comments || []);
    this.initForm();
    this.UserID = this.userService.getUserId();
    this.userInitials = this.userService.getUserInitials() || 'U';
    this.showComment = undefined;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['comments'] && !changes['comments'].isFirstChange()) {
      this.pagination = new Pagination<any>().deserialize(this.comments || []);
      this.initForm();
    }
  }

  getAuthorName(ctrlValue: any): string {
    if (!ctrlValue) return 'Utilizador';
    const nome =
      ctrlValue.utilizadorNome ||
      ctrlValue.userName ||
      ctrlValue.nome ||
      ctrlValue.utilizador?.nomeCompleto ||
      ctrlValue.utilizador?.nome ||
      ctrlValue.utilizadorNomeAnonimo;
    if (nome && typeof nome === 'string' && nome.trim()) {
      return nome.trim();
    }
    return 'Utilizador';
  }

  initForm() {
    const list = Array.isArray(this.comments) ? this.comments : [];
    this.formEditComment = this.fb.group({
      comments: this.fb.array(
        list.map((comment) =>
          this.fb.group({
            id: [comment.id],
            utilizadorID: [comment.utilizadorID],
            utilizadorNome: [
              comment.utilizadorNome ||
              comment.userName ||
              comment.nome ||
              comment.utilizador?.nomeCompleto ||
              comment.utilizador?.nome ||
              comment.utilizadorNomeAnonimo ||
              'Utilizador',
            ],
            utilizadorAnonimo: [comment.utilizadorAnonimo],
            utilizadorNomeAnonimo: [comment.utilizadorNomeAnonimo],
            campanhaID: [comment.campanhaID],
            conteudo: [comment.conteudo],
          }),
        ),
      ),
    });
  }

  get commentsArray(): FormArray<FormGroup> {
    return this.formEditComment.get('comments') as FormArray<FormGroup>;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (target.closest('.menu-action') || target.closest('.btn-options')) {
      return;
    }

    if (!target.closest('.comment_item')) {
      this.cancelEdit();
    }
  }

  enableEdit(commentForm: FormGroup, index: number) {
    commentForm.get('conteudo')?.enable();
    this.showComment = index;
  }

  cancelEdit() {
    this.showComment = undefined;
  }

  submitComment(commentForm: FormGroup) {
    const params = {
      id: commentForm.get('id')?.value,
      utilizadorID: this.UserID,
      campanhaID: commentForm.get('campanhaID')?.value,
      conteudo: commentForm.get('conteudo')?.value,
    };

    this.comentarioService.update(params).subscribe({
      next: (result: any) => {
        if (result.retorno.codigo === 200) {
          this.messageService.add({
            severity: 'success',
            summary: 'Comentário',
            detail: 'Seu comentário foi editado com sucesso',
            life: 3000,
          });
          this.showComment = undefined;
          this.commentChanged.emit();
        }
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Falha ao editar comentário',
          detail: 'Tente novamente mais tarde',
          life: 5000,
        });
        console.error(error);
      },
    });
    this.showComment = undefined;
  }

  deleteComment(id: any) {
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja eliminar seu comentário?',
      header: 'Confirmação',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Sim, eliminar',
        severity: 'info',
      },
      accept: () => {
        this.comentarioService.delete(id).subscribe((res: any) => {
          if (res.retorno.codigo === 200) {
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Comentário eliminado com sucesso',
              life: 3000,
            });
            this.commentChanged.emit();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Falha ao eliminar comentário',
              life: 5000,
            });
          }
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'warn',
          summary: 'Acção cancelada',
          detail: 'Nenhuma acção será realizada',
          life: 5000,
        });
      },
    });
  }

  submeterNovoComentario(): void {
    const conteudo = this.novoConteudoComentario?.trim();
    const targetCampanhaId = this.campanhaID || this.comments?.[0]?.campanhaID;
    if (!conteudo || this.isSubmittingNovo || !targetCampanhaId) {
      return;
    }
    if (!this.UserID) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Login necessário',
        detail: 'Faça login para comentar nesta campanha',
        life: 5000,
      });
      return;
    }

    this.isSubmittingNovo = true;
    const params = {
      Id: 0,
      utilizadorID: this.UserID,
      campanhaID: targetCampanhaId,
      conteudo: conteudo,
    };

    this.comentarioService.store(params).subscribe({
      next: (result: any) => {
        this.isSubmittingNovo = false;
        if (result.retorno.codigo === 200) {
          this.novoConteudoComentario = '';
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Comentário publicado com sucesso!',
            life: 3000,
          });
          this.commentChanged.emit();
        }
      },
      error: (error: any) => {
        this.isSubmittingNovo = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Operação não permitida',
          detail:
            error?.error?.retorno?.mensagem ||
            'Não é possível comentar nesta campanha porque já foi encerrada.',
          life: 5000,
        });
        console.error(error);
      },
    });
  }

  onNovoComentarioEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.isSubmittingNovo && this.novoConteudoComentario?.trim()) {
        this.submeterNovoComentario();
      }
    }
  }
}
